import { SimilarProduct } from '@noths/polaris-dev-ts-types';
import { createSlice, SerializedError } from '@reduxjs/toolkit';

import { Placement, PlacementType } from 'src/constants/recommendations';
import { similarProductsRequestComplete } from 'src/redux/richRelevance/actions';
import { logger } from 'src/utils/serverLogger';
import { requestSimilarProducts } from './thunks';

type Placements =
  | Placement.CategoryListingsRecentlyViewed
  | Placement.SearchListingsRecentlyViewed
  | Placement.PartnerListingsRecentlyViewed;

const placementList: Placements[] = [
  Placement.CategoryListingsRecentlyViewed,
  Placement.SearchListingsRecentlyViewed,
  Placement.PartnerListingsRecentlyViewed,
];

const defaultPlacement = { products: [] as SimilarProduct[] };

export type RichRelevancePlacements = Pick<PlacementType<SimilarProduct>, Placements>;

export interface RichRelevanceState extends RichRelevancePlacements {
  errors: SerializedError[] | null;
}

export const initialState: Readonly<RichRelevanceState> = {
  errors: null,
  [Placement.CategoryListingsRecentlyViewed]: defaultPlacement,
  [Placement.SearchListingsRecentlyViewed]: defaultPlacement,
  [Placement.PartnerListingsRecentlyViewed]: defaultPlacement,
};

export const { actions, reducer } = createSlice({
  name: 'richRelevance',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestSimilarProducts.fulfilled, (state, { payload }) => {
        if (payload && payload.placementType) {
          state[payload.placementType] = {
            placementTitle: payload.placementTitle,
            placementStrategy: payload.placementStrategy,
            products: payload.products.length > 0 ? payload.products : null,
          };
        }
      })
      .addCase(requestSimilarProducts.rejected, (state, action) => {
        if (action.payload?.error) {
          logger.error('Error requesting similar products', action.payload.error);

          state.errors = state.errors
            ? state.errors.concat([action.payload.error])
            : [action.payload.error];
        }

        if (action.payload?.placementType) {
          state[action.payload.placementType] = {
            products: null,
          };
        }
      })
      .addCase(similarProductsRequestComplete, (state) => {
        placementList.forEach((placement) => {
          if (state[placement]?.products?.length === 0) {
            state[placement].products = null;
          }
        });
      });
  },
});
